import { useCallback, useRef, useState } from 'react';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';
import { useCanUseBird } from '../../hooks/user/useCanUseBird';

/**
 * Who currently holds the single viewport column immediately right of the nav
 * rail. Exactly one of them can, which is why this is a slot and not two
 * booleans: "Bird and the workspace sidebar both open" is unrepresentable, so
 * no effect is needed to prevent it and no order of operations can
 * reintroduce it.
 *
 * The panels used to be independent `useDisclosure`s with pairwise "when A
 * opens, close B" effects bolted between them. That shape needs one edge per
 * ordered pair, and the drawer-opens-while-Bird-is-open edge was simply never
 * written — so both rendered at once and a 1440px viewport had nothing left
 * for the page.
 *
 * The SPLIT VIEW is deliberately NOT a member. It renders inside the page
 * content area, to the RIGHT of this column, so it can and should coexist with
 * the workspace sidebar. It still cannot coexist with Bird (they each want
 * ~40% of the viewport), so Layout keeps that one edge explicitly via
 * {@link UseLeftPanelResult.closeIfBird}.
 */
export type LeftPanel = 'none' | 'workspace' | 'bird';

/** The panels a caller can actually ask for — `close()` is how you get 'none'. */
export type LeftPanelSlot = Exclude<LeftPanel, 'none'>;

const PANELS: readonly LeftPanel[] = ['none', 'workspace', 'bird'];

interface UseLeftPanelOptions {
  readonly workspaceId: string | undefined;
  /**
   * Whether the user is viewing the workspace right now
   * (`useInWorkspaceContext`). The workspace slot is not a stored toggle: the
   * sidebar shows exactly when this is true and the user has not collapsed
   * it, Bower still takes the slot anywhere, and `close` on the sidebar
   * collapses it (a separate per-workspace preference) rather than emptying
   * the slot.
   */
  readonly workspaceContext: boolean;
  /**
   * A narrow window, where the sidebar overlays the canvas: it starts hidden
   * and opens only from the rail, and closing it is not saved as the
   * wide-screen collapse preference.
   */
  readonly narrow?: boolean;
}

export interface UseLeftPanelResult {
  /** The panel that may render RIGHT NOW — already read-only-guarded. */
  readonly panel: LeftPanel;
  readonly open: (panel: LeftPanelSlot) => void;
  readonly close: () => void;
  /** Asking for the panel that already holds the slot closes it. */
  readonly toggle: (panel: LeftPanelSlot) => void;
  /**
   * Vacate the slot only if Bird holds it. The one pairwise edge the slot
   * deliberately does not absorb — see the {@link LeftPanel} docblock.
   */
  readonly closeIfBird: () => void;
  /**
   * The rail's Workspace item: expand a collapsed sidebar and take the slot
   * back from Bower, so the workspace the rail navigates to shows its sidebar.
   * Tracked as a drawer open only when it was hidden.
   */
  readonly showWorkspace: () => void;
}

function storageKeyFor(workspaceId: string | undefined): string | undefined {
  return workspaceId ? `left-panel:${workspaceId}` : undefined;
}

function isLeftPanel(value: unknown): value is LeftPanel {
  return typeof value === 'string' && PANELS.includes(value as LeftPanel);
}

function collapsedKeyFor(workspaceId: string | undefined): string | undefined {
  return workspaceId ? `workspace-sidebar-collapsed:${workspaceId}` : undefined;
}

/**
 * Read one workspace's persisted value at `key`. Everything about the stored
 * value is untrusted (hand-edited, half-written, or left behind by an older
 * build whose panel names no longer exist), so a missing key, bad JSON or a
 * failed storage read gives `fallback`, and `parse` maps anything it does not
 * recognise to a safe value rather than crashing or selecting a panel that
 * cannot render.
 */
function readStored<T>(key: string | undefined, parse: (stored: unknown) => T, fallback: T): T {
  if (!key || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : parse(JSON.parse(raw));
  } catch {
    return fallback;
  }
}

function writeStored(key: string | undefined, value: unknown): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage disabled: degrade to session-only state.
    // There is no user-facing surface on which to report this.
  }
}

/**
 * `{ panel }` naming a known panel, else 'none'. A stored `'stream'` (the
 * retired capture stream panel) is not a known panel, so it reads as 'none'.
 */
function parsePanel(stored: unknown): LeftPanel {
  if (typeof stored !== 'object' || stored === null) return 'none';
  const { panel } = stored as { panel?: unknown };
  return isLeftPanel(panel) ? panel : 'none';
}

/** Anything but `{ collapsed: true }` is expanded. */
function parseCollapsed(stored: unknown): boolean {
  return (stored as { collapsed?: unknown } | null)?.collapsed === true;
}

const readPanel = (workspaceId: string | undefined) =>
  readStored(storageKeyFor(workspaceId), parsePanel, 'none');
const readCollapsed = (workspaceId: string | undefined) =>
  readStored(collapsedKeyFor(workspaceId), parseCollapsed, false);

/**
 * The exclusive left-panel slot, persisted per workspace.
 *
 * Persistence is written by the SETTERS, never by an effect on state. An effect
 * keyed on `[workspaceId, panel]` would fire once with the previous workspace's
 * panel under the new workspace's key on every switch — the exact bleed the
 * per-workspace key exists to prevent.
 *
 * Per workspace rather than per origin because which panel you want open is a
 * property of the work you are doing.
 *
 * That used to also be why a guest workspace could never inherit a Bird slot
 * from a workspace where you're a member — the old per-origin `birdPanelOpen`
 * key could, and a guest back then was fully excluded from Bird, so an
 * inherited 'bird' selection would have opened a panel they couldn't use.
 * That protection is no longer load-bearing: `canHoldBird` now admits every
 * resolved role, guests included (read-only), so an inherited 'bird'
 * selection would just open the panel they're now entitled to. The
 * per-workspace key stays right regardless, for the general reason above —
 * your open-panel preference in one workspace isn't a signal about another.
 */
export function useLeftPanel({
  workspaceId,
  workspaceContext,
  narrow = false,
}: UseLeftPanelOptions): UseLeftPanelResult {
  const [stored, setStored] = useState<LeftPanel>(() => readPanel(workspaceId));
  const [collapsed, setCollapsed] = useState(() => readCollapsed(workspaceId));
  // Narrow windows only, never persisted: the overlay is open for this session.
  const [overlayOpen, setOverlayOpen] = useState(false);

  // Switching workspaces loads that workspace's own preference. Adjusted
  // DURING RENDER (React's sanctioned prop-driven-reset pattern), not in an
  // effect: an effect leaves one committed frame showing the previous
  // workspace's panel around the new workspace's data before it fires.
  const loadedForRef = useRef(workspaceId);
  if (loadedForRef.current !== workspaceId) {
    loadedForRef.current = workspaceId;
    setStored(readPanel(workspaceId));
    setCollapsed(readCollapsed(workspaceId));
  }

  // Bird now admits resolved read-only roles too — writes are refused at the
  // MCP tool layer, not by hiding the Chat entry point. The shared capability
  // still recognises a proven workspace creator while a just-created
  // workspace is waiting for its role field, avoiding a dead Chat control
  // during that narrow initialization window.
  //
  // A MASK, not a mutation: `stored` is untouched, so nothing is discarded on a
  // transient. The moment the role settles the panel either appears (any
  // resolved role) or stays hidden (still resolving), and a role change is
  // reflected the next time the capability re-evaluates.
  const canHoldBird = useCanUseBird();
  // Only Bower is a stored choice; the sidebar follows the route.
  const sidebarShown = workspaceContext && (narrow ? overlayOpen : !collapsed);
  let panel: LeftPanel = sidebarShown ? 'workspace' : 'none';
  if (stored === 'bird' && canHoldBird) panel = 'bird';

  const commit = useCallback(
    (next: LeftPanel) => {
      setStored(next);
      writeStored(storageKeyFor(workspaceId), { panel: next });
    },
    [workspaceId],
  );

  const open = useCallback(
    (next: LeftPanelSlot) => {
      commit(next);
      // Opens are the engagement signal; closes are noise. Bird deliberately
      // has none here: `useBirdPanel`'s `chat:openPanel` listener tracks
      // `Bird Chat Opened` WITH the entry-point `source`, and every opener —
      // including the nav rail's Chat item, which dispatches the event with
      // source 'nav_rail' rather than toggling the slot directly — routes
      // through it, so tracking again here would double-count.
      if (next === 'workspace') {
        safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_DRAWER_OPENED, {});
      }
    },
    [commit],
  );

  const setCollapsedPreference = useCallback(
    (next: boolean) => {
      setCollapsed(next);
      writeStored(collapsedKeyFor(workspaceId), { collapsed: next });
    },
    [workspaceId],
  );

  // Closing the SIDEBAR is its collapse control, a preference kept apart from
  // the slot; closing Bower empties the slot as always.
  const close = useCallback(() => {
    if (panel === 'workspace') {
      if (narrow) setOverlayOpen(false);
      else setCollapsedPreference(true);
      return;
    }
    commit('none');
  }, [panel, narrow, setCollapsedPreference, commit]);

  // Every rail click calls this; only one that brings the sidebar back
  // (collapsed, or behind Bower) is an open worth tracking.
  const showWorkspace = useCallback(() => {
    const wasHidden = panel !== 'workspace';
    if (narrow) setOverlayOpen(true);
    else setCollapsedPreference(false);
    if (stored === 'bird') commit('workspace');
    if (wasHidden) {
      safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_DRAWER_OPENED, {});
    }
  }, [narrow, panel, setCollapsedPreference, stored, commit]);

  const toggle = useCallback(
    (next: LeftPanelSlot) => {
      if (panel === next) {
        close();
        return;
      }
      open(next);
    },
    [panel, close, open],
  );

  // Reads `stored`, not `panel`: a masked-but-stored Bird still has to be
  // cleared, or closing the split view later would resurrect it.
  const closeIfBird = useCallback(() => {
    if (stored === 'bird') close();
  }, [stored, close]);

  return { panel, open, close, toggle, closeIfBird, showWorkspace };
}
